(() => {
  if (customElements.get('euphoria-announcements')) return;

  const parseJst = (value) => {
    if (!value) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/.exec(value);
    if (!match) return NaN;
    const [year, month, day, hour, minute] = match.slice(1).map(Number);
    if (year < 1900 || month < 1 || month > 12 || day < 1 || hour > 23 || minute > 59) return NaN;
    const local = new Date(Date.UTC(year, month - 1, day, hour, minute));
    if (local.getUTCFullYear() !== year || local.getUTCMonth() !== month - 1 || local.getUTCDate() !== day) return NaN;
    return local.getTime() - 9 * 60 * 60 * 1000;
  };

  class EuphoriaAnnouncements extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.items = Array.from(this.querySelectorAll('[data-notice-item]')).map((element) => ({
        element,
        start: parseJst(element.dataset.start),
        end: parseJst(element.dataset.end),
        scope: element.dataset.scope,
      }));
      this.previous = this.querySelector('[data-notice-prev]');
      this.next = this.querySelector('[data-notice-next]');
      this.toggle = this.querySelector('[data-notice-pause]');
      this.playIcon = this.querySelector('[data-notice-play-icon]');
      this.messages = this.querySelector('.ea-announcements__messages');
      this.sectionContainer = this.closest('.shopify-section');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.auto = this.dataset.auto === 'true';
      this.interval = Math.max(8000, (Number(this.dataset.speed) || 10) * 1000);
      this.current = null;
      this.visibleItems = [];
      this.manualPause = false;
      this.editorSelected = false;
      this.hovered = false;
      this.focused = false;
      this.lastHeight = 0;
      const listen = (target, name, callback) => target.addEventListener(name, callback, { signal: this.abort.signal });
      listen(this.previous, 'click', () => this.move(-1));
      listen(this.next, 'click', () => this.move(1));
      listen(this.toggle, 'click', () => {
        this.manualPause = !this.manualPause;
        this.updatePlayback();
      });
      listen(this, 'pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        this.hovered = true;
        this.updatePlayback();
      });
      listen(this, 'pointerleave', () => {
        this.hovered = false;
        this.updatePlayback();
      });
      listen(this, 'focusin', () => {
        this.focused = true;
        this.updatePlayback();
      });
      listen(this, 'focusout', (event) => {
        this.focused = this.contains(event.relatedTarget);
        this.updatePlayback();
      });
      listen(document, 'visibilitychange', () => this.evaluateSchedule());
      listen(this.motion, 'change', () => this.updatePlayback());
      if (this.dataset.designMode === 'true') {
        listen(document, 'shopify:block:select', (event) => {
          const item = this.visibleItems.find((entry) => entry.element.dataset.blockId === event.detail.blockId);
          if (!item) return;
          this.editorSelected = true;
          this.show(item);
        });
        listen(document, 'shopify:block:deselect', () => {
          this.editorSelected = false;
          this.updatePlayback();
        });
      }
      this.evaluateSchedule();
    }

    disconnectedCallback() {
      this.abort?.abort();
      clearTimeout(this.rotationTimer);
      clearTimeout(this.scheduleTimer);
    }

    evaluateSchedule() {
      clearTimeout(this.scheduleTimer);
      const now = Date.now();
      const boundaries = [];
      this.visibleItems = this.items.filter((item) => {
        if (item.scope === 'home' && this.dataset.home !== 'true') return false;
        if (Number.isNaN(item.start) || Number.isNaN(item.end)) return false;
        if (item.start !== null && item.end !== null && item.end <= item.start) return false;
        if (item.start !== null && item.start > now) boundaries.push(item.start);
        if (item.end !== null && item.end > now) boundaries.push(item.end);
        return (item.start === null || item.start <= now) && (item.end === null || now < item.end);
      });
      const wasHidden = this.hidden;
      this.hidden = this.visibleItems.length === 0;
      if (this.sectionContainer) this.sectionContainer.hidden = this.hidden;
      this.previous.hidden = this.next.hidden = this.visibleItems.length < 2;
      if (!this.visibleItems.includes(this.current)) this.current = this.visibleItems[0] || null;
      this.show(this.current);
      if (boundaries.length) {
        this.scheduleTimer = setTimeout(() => this.evaluateSchedule(), Math.min(2147483000, Math.max(1, Math.min(...boundaries) - now + 50)));
      }
      if (wasHidden !== this.hidden) {
        requestAnimationFrame(() => {
          const header = document.querySelector('sticky-header');
          const headerSection = header?.closest('.section-header');
          const height = this.offsetHeight;
          if (Number.isFinite(header?.headerBounds?.top) && headerSection) {
            const delta = height - this.lastHeight;
            if (!headerSection.classList.contains('shopify-section-header-sticky') || window.scrollY === 0) {
              const rect = headerSection.getBoundingClientRect();
              header.headerBounds = { top: rect.top + window.scrollY, bottom: rect.bottom + window.scrollY };
            } else {
              header.headerBounds = { top: header.headerBounds.top + delta, bottom: header.headerBounds.bottom + delta };
            }
          }
          this.lastHeight = height;
          window.dispatchEvent(new Event('resize'));
        });
      }
    }

    show(item) {
      this.current = item;
      this.items.forEach((entry) => { entry.element.hidden = entry !== item; });
      this.updatePlayback();
    }

    move(direction) {
      if (this.visibleItems.length < 2) return;
      const index = this.visibleItems.indexOf(this.current);
      this.show(this.visibleItems[(index + direction + this.visibleItems.length) % this.visibleItems.length]);
    }

    updatePlayback() {
      clearTimeout(this.rotationTimer);
      const available = this.auto && this.visibleItems.length > 1 && !this.motion.matches;
      const paused = this.manualPause || this.focused || this.hovered || this.editorSelected || document.hidden;
      this.toggle.hidden = !available;
      this.toggle.setAttribute('aria-pressed', String(this.manualPause));
      this.toggle.setAttribute('aria-label', this.manualPause ? this.dataset.resumeLabel : this.dataset.pauseLabel);
      this.playIcon.textContent = this.manualPause ? '▷' : 'Ⅱ';
      this.messages.setAttribute('aria-live', available && !paused ? 'off' : 'polite');
      if (available && !paused && !this.hidden) {
        this.rotationTimer = setTimeout(() => this.move(1), this.interval);
      }
    }
  }

  customElements.define('euphoria-announcements', EuphoriaAnnouncements);
})();
